/// <amd-module name="Wit/WorkItemDataManager/CacheManagers/WorkItemMetadataCacheStampManager" />
import { equals } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { getTFSProject } from "Tfs/Platform/Page";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IDataSourceResponse, IWorkItemMetadataCacheData } from "../Contracts";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { publishEvent } from "VSS/Platform/Telemetry";
import { WITCommonConstants } from "Wit/Common/Generated/Constants";

const MetadataStampsContributionId = "ms.vss-work-web.work-item-cache-data-provider";

export class WorkItemMetadataCacheStampManager {
    public workItemTypesEtag: string | null;

    protected _workItemMetaDataCacheStamps: { [projectId: string]: { [stamp: string]: string } | null }; // a map from project Id to cache stamps
    private _cachedPromises: { [key: string]: Promise<void> } = {};
    private _pageContext: IVssPageContext;
    private _contributionService: IVssContributionService;

    constructor(pageContext: IVssPageContext) {
        this._workItemMetaDataCacheStamps = {};
        this._pageContext = pageContext;
        this._contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    }

     /**
     * Sort and concatenate set ids to generate key for constant sets, this behavior is similar to MVC caching
     * @param ids An array of identifiers for which to get the cached value
     */ 
    public makeCacheKey(ids: string[]):string {
        return ids?.length > 0 ? 
            ids.sort().reduce((prevValue, currentValue) => {
                return prevValue + currentValue;
            }) 
            : "_";
    }

    /**
     * Ensure the metadata stamps are ready for current project
     * @param projectId Current project Id.
     * @param forceRefresh If true, force a server request to re-populate the data provider data. Default is false.
     */
    public ensureStampsForCurrentProject(projectId: string, forceRefresh: boolean = false): Promise<void> {
        const projectKey = projectId.toUpperCase();
        return this._contributionService
            .getDataAsync<IDataSourceResponse<IWorkItemMetadataCacheData>>(MetadataStampsContributionId)
            .then(response => {
                if (response && response.data) {
                    this._setCacheStamps(response.data, projectKey);
                }
            });
    }

    /**
     * Get cache stamp for given meta data type
     * @param type
     * @param projectId
     */
    public getMetadataCacheStamp(type: string, projectId?: string): Promise<string | null> {
        const currentProjectId = getTFSProject(this._pageContext)?.id;
        const projectKey = (projectId || currentProjectId || "").toUpperCase();

        if (!projectKey) {
            // if a project id is not specified and if the currentpage is not project scoped, that means we cant get cachestamps, so return null
            return Promise.resolve(null);
        }

        const getCacheStamp = () => {
            const stamp = this._workItemMetaDataCacheStamps[projectKey] ? this._workItemMetaDataCacheStamps[projectKey]![type] : null;
            return stamp;
        };

        if (!this._workItemMetaDataCacheStamps[projectKey]) {
            if (!this._cachedPromises[projectKey]) {
                this._cachedPromises[projectKey] = this._ensureMetadataCacheStamps(projectKey, currentProjectId, projectId);
                return this._cachedPromises[projectKey].then(
                    () => {
                        delete this._cachedPromises[projectKey];
                        return getCacheStamp();
                    },
                    (error: Error) => {
                        delete this._cachedPromises[projectKey];
                        publishEvent(this._pageContext, WITCommonConstants.WorkItemTrackingArea, "getMetadataCacheStamp", {
                            error: error
                        });
                        return getCacheStamp();
                    }
                );
            } else {
                return this._cachedPromises[projectKey].then(
                    () => {
                        return getCacheStamp();
                    },
                    () => {
                        return getCacheStamp();
                    }
                );
            }
        }

        return Promise.resolve(getCacheStamp());
    }

    protected _ensureMetadataCacheStamps(projectKey: string, currentProjectId?: string, projectId?: string): Promise<void> {
        const isSameProject = !projectId || equals(projectId, currentProjectId || "", true);

        const setCacheStamps = (stampData: IWorkItemMetadataCacheData) => {
            this._workItemMetaDataCacheStamps[projectKey] = stampData ? stampData.workItemMetadataCacheStamp : null;
            this.workItemTypesEtag = stampData ? stampData.rawWorkItemTypesEtagForCI : null;
        };

        let requestProperties = undefined;

        if (!isSameProject && projectId) {
            requestProperties = { projectId: projectId };
        }

        this._pageContext
            .getService<IVssPerformanceService>("IVssPerformanceService")
            .addSplitTiming("WorkItemTracking.GetMetadataCacheStamp.FromPageData");
        return this._contributionService
            .getDataAsync<IDataSourceResponse<IWorkItemMetadataCacheData>>(MetadataStampsContributionId, requestProperties)
            .then(response => {
                if (response && response.data) {
                    setCacheStamps(response.data);
                }
            });
    }

    private _setCacheStamps(stampData: IWorkItemMetadataCacheData, projectKey: string): void {
        this._workItemMetaDataCacheStamps[projectKey] = stampData ? stampData.workItemMetadataCacheStamp : null;
        this.workItemTypesEtag = stampData ? stampData.rawWorkItemTypesEtagForCI : null;
    }

    /**
     * This methods updates the params for a rest call with the metadata cachestamp. If the stamps
     * are already downloaded the callback will be called synchronously otherwise it will be async.
     * @param type The type of stamp
     * @param params The existing params to merge with the retrieved stamp
     * @param continuation The function to call when stamp is retrieved
     * @param projectId This should be passed by only those metadata that have a project dependent cacheStamp. Like WorkItemTypes
     */
    public async addStampToParams(type: string, params: any, projectId?: string): Promise<{ [param: string]: any }> {
        const buildParams = (stamp: string) => {
            if (stamp) {
                if (params) {
                    params.stamp = stamp;
                    return params;
                } else {
                    return {
                        stamp: stamp
                    };
                }
            }

            return params;
        };

        const stamp = await this.getMetadataCacheStamp(type, projectId);

        if (!stamp) {
            return params;
        }

        return buildParams(stamp);
    }
}
